import { useMemo } from 'react'
import * as THREE from 'three'
import { Outlined } from '../render/Outlined'
import { Box, Slab } from '../render/parts'
import { GRID_COLOR, GRID_STEP, SHADOW_COLOR } from '../render/theme'
import { useRenderStyle } from '../render/style'
import type { RoomConfig, WallSide } from '../state/document'
import { WALL_SIDES } from '../state/document'
import { helperProps } from './helpers'
import type { ViewIndex } from './camera'

/* ------------------------------------------------------------- shadow */

const shadowCache = new Map<string, THREE.CanvasTexture>()

/**
 * A designer's drop shadow, baked once into a canvas.
 *
 * `ContactShadows` re-renders the scene into a depth target plus blur passes on
 * every frame and hugs the furniture, which reads as a 3D render. This reads as
 * an illustration, and costs nothing per frame.
 */
function platformShadowTexture(width: number, depth: number, radius: number, ink: string): THREE.CanvasTexture {
  const key = `${width.toFixed(2)}x${depth.toFixed(2)}r${radius.toFixed(2)}|${ink}`
  const cached = shadowCache.get(key)
  if (cached) return cached

  const pixelsPerMetre = 90
  const blur = 26
  const pad = blur * 3
  const w = Math.round(width * pixelsPerMetre)
  const h = Math.round(depth * pixelsPerMetre)

  const canvas = document.createElement('canvas')
  canvas.width = w + pad * 2
  canvas.height = h + pad * 2
  const context = canvas.getContext('2d')
  if (context) {
    context.filter = `blur(${blur}px)`
    context.fillStyle = ink
    context.beginPath()
    context.roundRect(pad, pad, w, h, radius * pixelsPerMetre)
    context.fill()
  }

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  shadowCache.set(key, texture)
  return texture
}

function PlatformShadow({ width, depth, radius, y }: { width: number; depth: number; radius: number; y: number }) {
  const theme = useRenderStyle().theme
  const texture = useMemo(
    () => platformShadowTexture(width, depth, radius, SHADOW_COLOR[theme]),
    [width, depth, radius, theme],
  )
  const scale = 1 + (26 * 3 * 2) / (width * 90)
  // Offset opposite the key light, so the shadow reads as cast rather than pasted.
  return (
    <mesh
      position={[-0.05, y, -0.04]}
      rotation={[-Math.PI / 2, 0, 0]}
      renderOrder={-1}
      // Never contoured, and never part of the camera fit: the blur padding
      // makes the plane much larger than the shadow it carries, which would
      // push the framing off-centre.
      userData={{ outline: false }}
      raycast={() => {}}
    >
      <planeGeometry args={[width * scale, depth * scale]} />
      <meshBasicMaterial map={texture} transparent depthWrite={false} toneMapped={false} />
    </mesh>
  )
}

/* --------------------------------------------------------------- grid */

function FloorGrid({ width, depth }: { width: number; depth: number }) {
  const theme = useRenderStyle().theme
  const geometry = useMemo(() => {
    const points: number[] = []
    const halfW = width / 2
    const halfD = depth / 2
    for (let x = -halfW; x <= halfW + 1e-6; x += GRID_STEP) {
      points.push(x, 0, -halfD, x, 0, halfD)
    }
    for (let z = -halfD; z <= halfD + 1e-6; z += GRID_STEP) {
      points.push(-halfW, 0, z, halfW, 0, z)
    }
    const buffer = new THREE.BufferGeometry()
    buffer.setAttribute('position', new THREE.Float32BufferAttribute(points, 3))
    return buffer
  }, [width, depth])

  return (
    <lineSegments geometry={geometry} position={[0, 0.003, 0]} {...helperProps} raycast={() => {}}>
      <lineBasicMaterial color={GRID_COLOR[theme]} transparent opacity={0.9} toneMapped={false} />
    </lineSegments>
  )
}

/* -------------------------------------------------------------- walls */

const WALL_THICKNESS = 0.07

/** Clear opening: wide enough to read at a glance, narrow enough for a 2.4 m wall. */
const DOOR_WIDTH = 0.95
const DOOR_HEIGHT = 2.1
/** Keeps the jamb off the corner, the way every floor plan draws it. */
const DOOR_INSET = 0.15
const DOOR_FRAME = 0.05
/** Below this the opening reads as a hole, not a door — a half wall gets none. */
const DOOR_MIN_HEIGHT = 1.6

/** For `wallMode: 'auto'`, the two walls furthest from the camera at each corner. */
const AUTO_WALLS: Record<ViewIndex, WallSide[]> = {
  0: ['north', 'west'],
  1: ['north', 'east'],
  2: ['south', 'east'],
  3: ['south', 'west'],
}

export function visibleWalls(room: RoomConfig, viewIndex: ViewIndex): Partial<Record<WallSide, RoomConfig['walls'][WallSide]>> {
  if (room.wallMode === 'manual') return room.walls
  const allowed = new Set(AUTO_WALLS[viewIndex])
  const result: Partial<Record<WallSide, RoomConfig['walls'][WallSide]>> = {}
  for (const side of WALL_SIDES) {
    result[side] = allowed.has(side) ? (room.walls[side] === 'none' ? 'solid' : room.walls[side]) : 'none'
  }
  return result
}

/** `[x, y, z]` for a point on a wall, given its position along the wall run. */
function onWall(horizontal: boolean, along: number, y: number): [number, number, number] {
  return horizontal ? [along, y, 0] : [0, y, along]
}

function spanning(horizontal: boolean, length: number, height: number, thickness: number): [number, number, number] {
  return horizontal ? [length, height, thickness] : [thickness, height, length]
}

/**
 * One stretch of wall. A wall with a door is three of these — the run either
 * side of the opening plus the header over it — so the opening is a real gap,
 * not a panel pasted on the surface.
 */
function WallPanel({
  horizontal,
  glass,
  length,
  height,
  along,
  bottom,
}: {
  horizontal: boolean
  glass: boolean
  length: number
  height: number
  along: number
  bottom: number
}) {
  if (length <= 0.001 || height <= 0.001) return null
  const y = bottom + height / 2

  const frame = 0.05
  // A stretch too short to carry a frame reads as a transom bar instead.
  if (!glass || height <= frame * 3 || length <= frame * 3) {
    return (
      <Box
        tone={glass ? 'metal' : 'wall'}
        size={spanning(horizontal, length, height, glass ? WALL_THICKNESS * 0.6 : WALL_THICKNESS)}
        position={onWall(horizontal, along, y)}
      />
    )
  }

  return (
    <group position={onWall(horizontal, along, y)}>
      {/* Slim frame: a bare glass tone alone reads as nothing on white. */}
      {[-1, 1].map((edge) => (
        <Box
          key={`rail${edge}`}
          tone="metal"
          size={spanning(horizontal, length, frame, WALL_THICKNESS * 0.6)}
          position={[0, edge * (height / 2 - frame / 2), 0]}
        />
      ))}
      {[-1, 1].map((end) => (
        <Box
          key={`mullion${end}`}
          tone="metal"
          size={spanning(horizontal, frame, height, WALL_THICKNESS * 0.6)}
          position={onWall(horizontal, end * (length / 2 - frame / 2), 0)}
        />
      ))}
      {/* The frame carries the contour; a contoured pane would double every edge. */}
      <Box
        tone="glass"
        size={spanning(horizontal, length - frame * 2, height - frame * 2, WALL_THICKNESS * 0.35)}
        outline={false}
      />
    </group>
  )
}

/** Frame, leaf and handle sitting inside the opening the wall left for them. */
function DoorLeaf({
  horizontal,
  glass,
  width,
  height,
  along,
}: {
  horizontal: boolean
  glass: boolean
  width: number
  height: number
  along: number
}) {
  const leafWidth = width - DOOR_FRAME * 2
  const leafHeight = height - DOOR_FRAME
  // Handle on the opening's inner edge: the door is inset from the corner, so
  // that is the side a person actually reaches for.
  const handleAlong = along - leafWidth / 2 + 0.12

  return (
    <group>
      {[-1, 1].map((edge) => (
        <Box
          key={`jamb${edge}`}
          tone="panel"
          size={spanning(horizontal, DOOR_FRAME, height, WALL_THICKNESS)}
          position={onWall(horizontal, along + edge * (width - DOOR_FRAME) / 2, height / 2)}
        />
      ))}
      <Box
        tone="panel"
        size={spanning(horizontal, width, DOOR_FRAME, WALL_THICKNESS)}
        position={onWall(horizontal, along, height - DOOR_FRAME / 2)}
      />
      <Box
        tone={glass ? 'glass' : 'shell'}
        outline={!glass}
        size={spanning(horizontal, leafWidth, leafHeight, WALL_THICKNESS * 0.5)}
        position={onWall(horizontal, along, leafHeight / 2)}
      />
      <Box
        tone="metalDark"
        size={spanning(horizontal, 0.13, 0.03, WALL_THICKNESS * 1.1)}
        position={onWall(horizontal, handleAlong, 1.05)}
      />
    </group>
  )
}

function Wall({
  side,
  style,
  hasDoor,
  room,
}: {
  side: WallSide
  style: RoomConfig['walls'][WallSide]
  hasDoor: boolean
  room: RoomConfig
}) {
  if (style === 'none') return null

  const height = style === 'half' ? room.wallHeight * 0.45 : room.wallHeight
  const glass = style === 'glass'
  const horizontal = side === 'north' || side === 'south'

  // North/south walls run the full width and swallow the corners; east/west stop
  // at the interior so exactly one clean end face shows at each corner.
  const length = horizontal ? room.width + WALL_THICKNESS * 2 : room.depth
  const position: [number, number, number] = horizontal
    ? [0, 0, (side === 'north' ? -1 : 1) * (room.depth / 2 + WALL_THICKNESS / 2)]
    : [(side === 'west' ? -1 : 1) * (room.width / 2 + WALL_THICKNESS / 2), 0, 0]

  const doorHeight = Math.min(DOOR_HEIGHT, height - 0.15)
  const doorWidth = Math.min(DOOR_WIDTH, length - DOOR_INSET * 2)
  const door = hasDoor && doorHeight >= DOOR_MIN_HEIGHT && doorWidth >= 0.6

  if (!door) {
    return (
      <group position={position}>
        <WallPanel
          horizontal={horizontal}
          glass={glass}
          length={length}
          height={height}
          along={0}
          bottom={0}
        />
      </group>
    )
  }

  // The opening sits at the far end of the run, clear of the furniture that
  // lines the middle of the wall.
  const openingCentre = length / 2 - DOOR_INSET - doorWidth / 2
  const openingStart = openingCentre - doorWidth / 2
  const openingEnd = openingCentre + doorWidth / 2

  return (
    <group position={position}>
      <WallPanel
        horizontal={horizontal}
        glass={glass}
        length={openingStart + length / 2}
        height={height}
        along={(openingStart - length / 2) / 2}
        bottom={0}
      />
      <WallPanel
        horizontal={horizontal}
        glass={glass}
        length={length / 2 - openingEnd}
        height={height}
        along={(openingEnd + length / 2) / 2}
        bottom={0}
      />
      <WallPanel
        horizontal={horizontal}
        glass={glass}
        length={doorWidth}
        height={height - doorHeight}
        along={openingCentre}
        bottom={doorHeight}
      />
      <DoorLeaf
        horizontal={horizontal}
        glass={glass}
        width={doorWidth}
        height={doorHeight}
        along={openingCentre}
      />
    </group>
  )
}

/* --------------------------------------------------------------- room */

export interface RoomProps {
  room: RoomConfig
  viewIndex: ViewIndex
}

export function Room({ room, viewIndex }: RoomProps) {
  const platformWidth = room.width + room.platformMargin * 2
  const platformDepth = room.depth + room.platformMargin * 2
  const walls = visibleWalls(room, viewIndex)

  return (
    <group>
      {room.showShadow ? (
        <PlatformShadow
          width={platformWidth}
          depth={platformDepth}
          radius={room.platformRadius}
          y={-room.platformThickness - 0.012}
        />
      ) : null}

      <Outlined>
        <Slab
          tone="floor"
          size={[platformWidth, platformDepth, room.platformThickness]}
          radius={room.platformRadius}
          position={[0, -room.platformThickness / 2, 0]}
        />
        {WALL_SIDES.map((side) => (
          <Wall
            key={side}
            side={side}
            style={walls[side] ?? 'none'}
            hasDoor={room.door === side}
            room={room}
          />
        ))}
      </Outlined>

      {room.showGrid ? <FloorGrid width={room.width} depth={room.depth} /> : null}
    </group>
  )
}
